import * as React from 'react';
import {
  Link,
  RouteComponentProps,
  Switch,
  Route,
  withRouter,
} from 'react-router-dom';
import { GraphQLErrorDialog } from '../components/Dialogs';
import { useGetPlaceQuery } from '../generated/graphql';
import { PlaceCreatePage } from './PlaceCreatePage';
import { PlaceIndexPage } from './PlaceIndexPage';
import { PlaceCodesPage } from './PlaceCodesPage';
import { PlaceCreateCodePage } from './PlaceCreateCodePage';
import { PlaceCreateSortPage } from './PlaceCreateSortPage';
import { PlaceSortsPage } from './PlaceSortsPage';
import { PlaceCodeAndSortPage } from './PlaceCodeAndSortPage';
import { PlaceCreateCodeAndSortPage } from './PlaceCreateCodeAndSortPage';
import { PlaceWorkListPage } from './PlaceWorkListPage';
import { PlaceCreateWorkPage } from './PlaceCreateWorkPage';
import { PlaceDxfListPage } from './PlaceDxfListPage';
import { PlaceDxfUploadPage } from './PlaceDxfUploadPage';
import { PlaceDxfPicturesPage } from './PlaceDxfPicturesPage';
import { PlaceDxfMarkersPage } from './PlaceDxfMarkersPage';
import { NotFoundPage } from './NotFoundPage';
import { Loading } from '../Loading';
import { PageHeader } from '../components/PageHeader';
import './PlacePage.css';

type IPlacePageProps = RouteComponentProps<{ id: string }> & {};

const navPathRegex = /^\/places\/[0-9]+\/([^/]+)(\/.+)*$/;

interface PlacePageTab {
  /** URLの物件IDの次の部分。基本情報のタブはnull */
  subpath: string | null;
  label: string;
  to: string;
  /** 指定したときだけ件数のバッジを出す */
  count?: number;
}

/**
 * URLから選択中のタブを求める。物件情報の編集画面（/edit）は基本情報のタブの中の画面として扱う。
 */
function getSelectedSubpath(path: string): string | null {
  const match = navPathRegex.exec(path);
  if (!match) {
    return null;
  }
  if (match[1] === 'edit') {
    return null;
  }
  return match[1];
}

function getTabClassName(selected: boolean): string {
  if (selected) {
    return 'PlacePage-navigator_selected';
  }
  return '';
}

function getTabAriaCurrent(selected: boolean): 'page' | undefined {
  if (selected) {
    return 'page';
  }
  return undefined;
}

const PlacePageTabCount: React.FC<{ count?: number }> = function (props) {
  if (props.count === undefined) {
    return null;
  }
  let className = 'PlacePage-navigator_items_count';
  if (props.count === 0) {
    className += ' PlacePage-navigator_items_count_empty';
  }
  return <span className={className}>{props.count}</span>;
};

/**
 * 物件ページ
 */
export const PlacePage: React.FC<IPlacePageProps> = function (props) {
  const placeId = parseInt(props.match.params.id);
  const getPlaceResult = useGetPlaceQuery({
    variables: {
      placeId,
    },
  });

  if (getPlaceResult.error) {
    return <GraphQLErrorDialog error={getPlaceResult.error} />;
  }

  if (getPlaceResult.loading || !getPlaceResult.data) {
    return <Loading />;
  }

  const subpath = getSelectedSubpath(props.location.pathname);
  const place = getPlaceResult.data.getPlace;

  // 件数を出せるのは、物件の取得結果に一覧が含まれているタブだけ（工種と分類×コードは含まれていない）
  const tabs: PlacePageTab[] = [
    { subpath: null, label: '基本情報', to: `/places/${placeId}` },
    { subpath: 'sorts', label: '補修分類', to: `/places/${placeId}/sorts`, count: place.sorts.length },
    { subpath: 'codes', label: '補修コード', to: `/places/${placeId}/codes`, count: place.codes.length },
    { subpath: 'codes_and_sorts', label: '分類 × コード', to: `/places/${placeId}/codes_and_sorts` },
    { subpath: 'works', label: '工種', to: `/places/${placeId}/works` },
    { subpath: 'faces', label: '図面', to: `/places/${placeId}/faces`, count: place.faces.length },
  ];

  return (
    <div className="PlacePage-container">
      <PageHeader
        title={place.name}
        breadcrumbs={[{ label: '物件一覧', to: '/places' }, { label: place.name }]}
      />
      <div className="PlacePage-navigator">
        <nav aria-label="物件の情報の切り替え">
          <ul>
            {tabs.map((tab) => {
              const selected = tab.subpath === subpath;
              return (
                <li key={tab.label} className={getTabClassName(selected)}>
                  <Link
                    className="PlacePage-navigator_item"
                    to={tab.to}
                    aria-current={getTabAriaCurrent(selected)}
                  >
                    <span className="PlacePage-navigator_item_name">{tab.label}</span>
                    <PlacePageTabCount count={tab.count} />
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
      <div>
        <Switch>
          <Route
            exact
            path="/places/:placeId"
            component={withRouter((props) => (
              <PlaceIndexPage {...props} />
            ))}
          />
          <Route
            path="/places/:placeId/edit"
            component={withRouter((props) => (
              <PlaceCreatePage
                {...props}
                existingId={parseInt(props.match.params.placeId)}
              />
            ))}
          />
          <Route
            exact
            path="/places/:placeId/codes"
            component={withRouter((props) => (
              <PlaceCodesPage {...props} />
            ))}
          />
          <Route
            exact
            path="/places/:placeId/codes/new"
            component={withRouter((props) => (
              <PlaceCreateCodePage {...props} />
            ))}
          />
          <Route
            exact
            path="/places/:placeId/codes/:codeId/edit"
            component={withRouter((props) => (
              <PlaceCreateCodePage
                {...props}
                existingId={parseInt(props.match.params.codeId)}
              />
            ))}
          />
          <Route
            exact
            path="/places/:placeId/sorts"
            component={withRouter((props) => (
              <PlaceSortsPage {...props} />
            ))}
          />
          <Route
            exact
            path="/places/:placeId/sorts/new"
            component={withRouter((props) => (
              <PlaceCreateSortPage {...props} />
            ))}
          />
          <Route
            exact
            path="/places/:placeId/sorts/:sortId/edit"
            component={withRouter((props) => (
              <PlaceCreateSortPage
                {...props}
                existingId={parseInt(props.match.params.sortId)}
              />
            ))}
          />
          <Route
            exact
            path="/places/:placeId/codes_and_sorts"
            component={withRouter((props) => (
              <PlaceCodeAndSortPage {...props} />
            ))}
          />
          <Route
            exact
            path="/places/:placeId/codes_and_sorts/:codeId/:sortId/:gteEverguardThreshold/edit"
            component={withRouter((props) => (
              <PlaceCreateCodeAndSortPage {...props} />
            ))}
          />
          <Route
            exact
            path="/places/:placeId/works"
            component={withRouter((props) => (
              <PlaceWorkListPage {...props} />
            ))}
          />
          <Route
            exact
            path="/places/:placeId/works/new"
            component={withRouter((props) => (
              <PlaceCreateWorkPage {...props} />
            ))}
          />
          <Route
            exact
            path="/places/:placeId/works/:workId/edit"
            component={withRouter((props) => (
              <PlaceCreateWorkPage {...props} />
            ))}
          />
          <Route
            exact
            path="/places/:placeId/faces"
            component={withRouter((props) => (
              <PlaceDxfListPage {...props} />
            ))}
          />
          <Route
            exact
            path="/places/:placeId/faces/:placeFaceId/pictures"
            component={withRouter((props) => (
              <PlaceDxfPicturesPage {...props} />
            ))}
          />
          <Route
            exact
            path="/places/:placeId/faces/:placeFaceId/markers"
            component={withRouter((props) => (
              <PlaceDxfMarkersPage {...props} />
            ))}
          />
          <Route
            exact
            path="/places/:placeId/faces/new"
            component={withRouter((props) => (
              <PlaceDxfUploadPage {...props} />
            ))}
          />
          <Route
            exact
            path="/places/:placeId/faces/:placeFaceId/edit"
            component={withRouter((props) => (
              <PlaceDxfUploadPage
                {...props}
                existingId={parseInt(props.match.params.placeFaceId)}
              />
            ))}
          />
          <Route component={() => <NotFoundPage />} />
        </Switch>
      </div>
    </div>
  );
};
